import classNames from 'classnames';
import { type ReactNode, useMemo } from 'react';

import { HoverCardWithIcon } from '@/common/components/shared/hover-card/HoverCardWithIcon';

import type { Suggestion } from '@/common/types';

interface SuggestionHintProps<W extends { percentage: string | number }, S extends Suggestion> {
  show: boolean;
  suggestions: S[];
  watched: W[];
  isItemDifferent: (w: W, s: S) => boolean;
  renderName: (s: S) => ReactNode;
  getTooltipSources?: (s: S) => string[];
}

export function SuggestionHint<W extends { percentage: string | number }, S extends Suggestion>({
  show,
  suggestions,
  watched,
  isItemDifferent,
  renderName,
  getTooltipSources,
}: SuggestionHintProps<W, S>) {
  const isDifferent = useMemo(() => {
    if (suggestions.length === 0) {
      return false;
    }

    return (
      watched.length !== suggestions.length ||
      suggestions.some(s => !watched.some(w => Number(w.percentage) === s.percentage && !isItemDifferent(w, s)))
    );
  }, [isItemDifferent, suggestions, watched]);

  if (!show || suggestions.length === 0) return null;

  return (
    <div className="mt-0.5 text-2xs">
      <div className={classNames({ 'text-orange-500': isDifferent })}>
        Suggestion
        {suggestions.length > 1 && suggestions.every(v => v.days != null) && (
          <span className="tabular-nums">&nbsp;(total {suggestions.reduce((s, v) => s + (v.days ?? 0), 0)} days)</span>
        )}
        :
      </div>
      <ul className="list-disc ml-6">
        {suggestions.map((s, i) => {
          const tooltipSources = getTooltipSources ? getTooltipSources(s) : s.suggestionSourceNames;
          return (
            <li key={i}>
              <div className="flex items-center gap-1">
                <div>
                  <span>{renderName(s)}:&nbsp;</span>
                  <span className="tabular-nums">{s.percentage}%</span>
                  {s.days != null && suggestions.length > 1 && (
                    <span className="tabular-nums">&nbsp;({s.days} days)</span>
                  )}
                </div>
                {tooltipSources.length > 0 && (
                  <HoverCardWithIcon>
                    <ul className="list-disc ml-4">
                      {tooltipSources.map((it, k) => (
                        <li key={k}>{it}</li>
                      ))}
                    </ul>
                  </HoverCardWithIcon>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
